import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { FinetuneRunNew } from "../../../model_gen/finetuning";
import { CompositionService } from "../../../services/CompositionService";

// Step components
import ChooseCompositionStep from "./Steps/ChooseCompositionStep";
import FinetuneDetailsStep from "./Steps/FinetuneDetailsStep";
import FinetuneSettingsStep from "./Steps/FinetuneSettingsStep";
import ReviewStep from "./Steps/ReviewStep";
import { NEW_FINETUNE_STORAGE_KEY } from "./common";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  RocketIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification } from "antd";

export interface FinetuneData {
  composition_id?: number;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  prompt?: string;
  fourbit?: boolean;
  base_model?: string;
  display_name?: string;
  description?: string;
  prompt_deletions?: string[];
  warmup_steps?: number;
  warmup_ratio?: number;
  learning_rate?: number;
  per_device_train_batch_size?: number;
  gradient_accumulation_steps?: number;
  train_on_responses_only?: boolean;
  train_split?: number;
  alpha?: number;
  rank?: number;
  epochs?: number;
  weight_decay?: number;
  dropout?: number;
  rslora?: boolean;
}

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const NewFinetuneRun: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [compositionLoading, setCompositionLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  // Get composition_id from URL if present
  const compositionIdFromUrl = searchParams.get("composition_id");

  // Initialize finetune data from localStorage or with defaults
  const [finetuneData, setFinetuneData] = useState<FinetuneData>(() => {
    const saved = localStorage.getItem(NEW_FINETUNE_STORAGE_KEY);
    const savedData = saved
      ? JSON.parse(saved)
      : {
          fourbit: false,
          warmup_steps: 5,
          warmup_ratio: 0.03,
          learning_rate: 1e-4,
          per_device_train_batch_size: 8,
          gradient_accumulation_steps: 4,
          train_split: 0.98,
          prompt_deletions: [],
          alpha: 16,
          rank: 16,
          epochs: 1,
          weight_decay: 0,
          dropout: 0.05,
          rslora: false,
          train_on_responses_only: false,
        };

    // If composition_id is in URL, use it to override saved data
    if (compositionIdFromUrl) {
      savedData.composition_id = parseInt(compositionIdFromUrl);

      // If composition is provided, start at step 1 (Finetune Details)
      if (!searchParams.has("step")) {
        setCurrentStep(1);
        // Don't immediately update URL here - let the useEffect handle it
      }
    }

    return savedData;
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_FINETUNE_STORAGE_KEY, JSON.stringify(finetuneData));
  }, [finetuneData]);

  // Update URL when step changes, preserving composition_id if present
  useEffect(() => {
    // Only update URL if step parameter should be present (when step > 0 or step was already in URL initially)
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      const newParams: Record<string, string> = {
        ...currentParams,
        step: currentStep.toString()
      };
      if (compositionIdFromUrl) {
        newParams.composition_id = compositionIdFromUrl;
      }
      setSearchParams(newParams, { replace: true }); // Use replace to avoid creating new history entries for step changes
    }
  }, [currentStep, searchParams, setSearchParams, compositionIdFromUrl]);

  // Fetch composition details if composition_id is provided in URL
  useEffect(() => {
    if (compositionIdFromUrl && finetuneData.composition_id) {
      setCompositionLoading(true);
      CompositionService.getComposition(
        compositionIdFromUrl,
        setCompositionLoading,
        apiKey
      ).subscribe(
        (composition: any) => {
          if (composition) {
            // Update finetuneData with additional composition information
            setFinetuneData((prev) => ({
              ...prev,
              application_id: composition.application_id,
              intent_id: composition.intent_id,
              intent_group_id: composition.intent_group_id,
              // Set a default display name based on the composition name
              display_name: prev.display_name || `${composition.name} ft`,
            }));
          }
        },
        (error: Error) => {
          console.error("Error fetching composition details:", error);
          notification.error({
            message: "Error",
            description: "Failed to fetch composition details",
          });
          setCompositionLoading(false);
        }
      );
    }
  }, [compositionIdFromUrl, apiKey, finetuneData.composition_id]);

  const steps: StepItem[] = [
    {
      key: "choose-composition",
      title: "Choose Composition",
      component: (
        <ChooseCompositionStep
          selectedComposition={finetuneData.composition_id?.toString() || ""}
          setSelectedComposition={(value: string) =>
            setFinetuneData((prev) => ({
              ...prev,
              composition_id: parseInt(value),
            }))
          }
          isCreatingFinetune={loading}
          setFinetuneData={setFinetuneData}
        />
      ),
    },
    {
      key: "finetune-settings",
      title: "Finetune Settings",
      component: (
        <FinetuneSettingsStep
          datasetId={finetuneData.composition_id} // Pass composition_id as datasetId for compatibility
          exportPrompt={finetuneData.prompt || ""}
          setExportPrompt={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, prompt: value }))
          }
          fourbit={finetuneData.fourbit || false}
          setFourbit={(value: boolean) =>
            setFinetuneData((prev) => ({ ...prev, fourbit: value }))
          }
          baseModel={finetuneData.base_model || ""}
          setBaseModel={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, base_model: value }))
          }
          promptDeletions={finetuneData.prompt_deletions || []}
          setPromptDeletions={(value: string[]) =>
            setFinetuneData((prev) => ({ ...prev, prompt_deletions: value }))
          }
          warmupSteps={finetuneData.warmup_steps || 5}
          setWarmupSteps={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, warmup_steps: value }))
          }
          warmupRatio={finetuneData.warmup_ratio || 0.03}
          setWarmupRatio={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, warmup_ratio: value }))
          }
          learningRate={finetuneData.learning_rate || 2e-4}
          setLearningRate={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, learning_rate: value }))
          }
          perDeviceTrainBatchSize={
            finetuneData.per_device_train_batch_size || 8
          }
          setPerDeviceTrainBatchSize={(value: number) =>
            setFinetuneData((prev) => ({
              ...prev,
              per_device_train_batch_size: value,
            }))
          }
          gradientAccumulationSteps={
            finetuneData.gradient_accumulation_steps || 4
          }
          setGradientAccumulationSteps={(value: number) =>
            setFinetuneData((prev) => ({
              ...prev,
              gradient_accumulation_steps: value,
            }))
          }
          trainOnResponsesOnly={finetuneData.train_on_responses_only || false}
          setTrainOnResponsesOnly={(value: boolean) =>
            setFinetuneData((prev) => ({
              ...prev,
              train_on_responses_only: value,
            }))
          }
          trainSplit={finetuneData.train_split || 0.8}
          setTrainSplit={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, train_split: value }))
          }
          alpha={finetuneData.alpha || 1}
          setAlpha={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, alpha: value }))
          }
          rank={finetuneData.rank || 1}
          setRank={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, rank: value }))
          }
          epochs={finetuneData.epochs || 1}
          setEpochs={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, epochs: value }))
          }
          weightDecay={finetuneData.weight_decay || 0}
          setWeightDecay={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, weight_decay: value }))
          }
          dropout={finetuneData.dropout || 0.05}
          setDropout={(value: number) =>
            setFinetuneData((prev) => ({ ...prev, dropout: value }))
          }
          rslora={finetuneData.rslora || false}
          setRslora={(value: boolean) =>
            setFinetuneData((prev) => ({ ...prev, rslora: value }))
          }
          selectedSubDataset={null} // Not used for compositions
          setSelectedSubDataset={() => {}} // Not used for compositions
          isExporting={loading}
        />
      ),
    },
    {
      key: "finetune-details",
      title: "Finetune Details",
      component: (
        <FinetuneDetailsStep
          displayName={finetuneData.display_name || ""}
          setDisplayName={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, display_name: value }))
          }
          description={finetuneData.description || ""}
          setDescription={(value: string) =>
            setFinetuneData((prev) => ({ ...prev, description: value }))
          }
          isExporting={loading}
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: (
        <ReviewStep
          selectedDataset={finetuneData.composition_id?.toString() || ""}
          exportPrompt={finetuneData.prompt || ""}
          fourbit={finetuneData.fourbit || false}
          baseModel={finetuneData.base_model || ""}
          displayName={finetuneData.display_name || ""}
          description={finetuneData.description || ""}
          promptDeletions={finetuneData.prompt_deletions || []}
          warmupSteps={finetuneData.warmup_steps || 5}
          warmupRatio={finetuneData.warmup_ratio || 0.03}
          learningRate={finetuneData.learning_rate || 2e-4}
          perDeviceTrainBatchSize={
            finetuneData.per_device_train_batch_size || 8
          }
          gradientAccumulationSteps={
            finetuneData.gradient_accumulation_steps || 4
          }
          trainOnResponsesOnly={finetuneData.train_on_responses_only || false}
          trainSplit={finetuneData.train_split || 0.8}
          alpha={finetuneData.alpha || 1}
          rank={finetuneData.rank || 1}
          epochs={finetuneData.epochs || 1}
          weightDecay={finetuneData.weight_decay || 0}
          dropout={finetuneData.dropout}
          selectedSubDataset={null}
          rslora={finetuneData.rslora || false}
        />
      ),
    },
  ];

  const canProceedFromStep = (stepIndex: number) => {
    // Don't allow proceeding if composition is still loading
    if (compositionLoading) return false;

    switch (stepIndex) {
      case 0:
        return finetuneData.composition_id !== undefined;
      case 1:
        return (
          finetuneData.base_model !== undefined &&
          finetuneData.base_model.trim().length > 0
        );
      case 2:
        return (
          finetuneData.display_name !== undefined &&
          finetuneData.display_name.trim().length > 0
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  const canProceed = () => canProceedFromStep(currentStep);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createFinetuneRun = () => {
    if (!finetuneData.composition_id) {
      notification.error({
        message: "Missing composition",
        description: "Please select a composition for the finetune run",
      });
      return;
    }

    setLoading(true);

    // Create the meta object
    const meta: any = {
      warmup_steps: finetuneData.warmup_steps,
      warmup_ratio: finetuneData.warmup_ratio,
      learning_rate: finetuneData.learning_rate,
      per_device_train_batch_size: finetuneData.per_device_train_batch_size,
      gradient_accumulation_steps: finetuneData.gradient_accumulation_steps,
      train_on_responses_only: finetuneData.train_on_responses_only,
      train_split: finetuneData.train_split,
      display_name: finetuneData.display_name,
      alpha: finetuneData.alpha,
      rank: finetuneData.rank,
      num_train_epochs: finetuneData.epochs,
      weight_decay: finetuneData.weight_decay,
      dropout: finetuneData.dropout,
      rslora: finetuneData.rslora,
    };

    // Create the FinetuneRunNew object
    const finetuneRun: FinetuneRunNew = {
      display_name: finetuneData.display_name || "",
      description: finetuneData.description || "",
      company_id: 0, // This will be set from the auth token on the server
      dataset_composition_id: finetuneData.composition_id,
      application_id: finetuneData.application_id,
      intent_id: finetuneData.intent_id,
      intent_group_id: finetuneData.intent_group_id,
      run_version: 0,
      status: "PENDING",
      base_model: finetuneData.base_model || "",
      prompt: finetuneData.prompt,
      remove_examples: false,
      remove_example_tag: false,
      remove_output_tag: false,
      fourbit: finetuneData.fourbit || false,
      conversation_mode: false,
      prompt_deletions: finetuneData.prompt_deletions || [],
      meta: meta,
    };

    FinetuneRunService.createFinetuneRun(
      finetuneRun,
      setLoading,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Finetune run created successfully",
          description: "Your finetune run has been created successfully",
        });
        localStorage.removeItem(NEW_FINETUNE_STORAGE_KEY);
        navigate("/forge/finetuning");
      },
      (error: Error) => {
        console.error("Error creating finetune run:", error);
        notification.error({
          message: "Failed to create finetune run",
          description:
            error.message ||
            "An error occurred while creating the finetune run",
        });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    // Check if each previous step would be valid without causing re-renders
    return canProceedFromStep(index);
  }).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/forge/finetuning")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Finetune Runs
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">New Run</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Finetune Run
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new fine-tuning run from a composition dataset
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" : 
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep
                        ? "text-maitai-green"
                        : index < currentStep
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep
                        ? "bg-maitai-green text-white"
                        : index < currentStep
                        ? "bg-maitai-green text-white"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? (
                        <CheckIcon className="w-3 h-3" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {compositionLoading && currentStep === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-maitai-green mb-4" />
                <div className="space-y-2">
                  <h3 className="text-lg font-gc-med text-foreground">
                    Loading composition information...
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Please wait while we fetch the composition details
                  </p>
                </div>
              </div>
            ) : (
              steps[currentStep].component
            )}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading || compositionLoading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading || compositionLoading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading || compositionLoading}
                    onClick={createFinetuneRun}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating finetune run..." : (
                      <>
                        <RocketIcon className="w-4 h-4" />
                        Create Finetune Run
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewFinetuneRun;
